import { z } from 'zod';
/**
 * Uzenet-dokumentum sema (ProseMirror/TipTap alaku JSON).
 *
 * BIZTONSAGI SZEREPE VAN: a szerver NEM bizhat abban, amit a kliens kuld.
 * Csak az itt felsorolt csomopont- es jelolestipusok engedettek; barmi mas
 * elutasitasra kerul. Igy nem lehet tetszoleges tartalmat becsempeszni a
 * dokumentumba, amit aztan minden kliens megjelenitene.
 *
 * A melyseg szandekosan FELULROL KORLATOS (lasd `listsOf`): nem lehet a
 * szervert melyen agyazott dokumentummal terhelni.
 */
/**
 * Ket kulon korlat: a PROZA es a KOD nem ugyanaz a mufaj.
 *
 * 12 000 jel folyoszovegbol tobb, mint amennyit barki elolvas egy chatben -
 * ott a korlat vedelem. Egy beillesztett stack trace vagy egy tabla-definicio
 * viszont konnyen atlepi, es olyankor a korlat nem ved, hanem akadalyoz: a
 * kollega ketfele vagja, vagy fajlt csatol - mindketto rosszabb, mint ha
 * egyben latnank.
 */
export const MAX_PROSE_LENGTH = 12_000;
export const MAX_CODE_LENGTH = 100_000;
/** Egyetlen szoveg-csomopont felso hatara - a nagyobbik korlat szabja meg. */
export const MAX_DOC_TEXT_LENGTH = MAX_CODE_LENGTH;
/** A szarmaztatott sima szoveg (kereses, ertesites) felso hatara. */
export const MAX_STORED_TEXT_LENGTH = MAX_PROSE_LENGTH + MAX_CODE_LENGTH;
export const MAX_DOC_NODES = 2_000;
// ─── Jelolesek ────────────────────────────────────────────────────────────────
/**
 * Csak http, https es mailto. A `javascript:` es a `data:` semat mar a
 * hatarnal elutasitjuk, hogy ne is kerulhessen az adatbazisba - a megjelenites
 * oldalan ez mar csak masodik vedvonal lenne.
 */
export const SAFE_URL_RE = /^(https?:\/\/|mailto:)/i;
const linkMark = z.object({
    type: z.literal('link'),
    attrs: z.object({
        href: z
            .string()
            .max(2048)
            .regex(SAFE_URL_RE, 'Csak http, https vagy mailto link engedélyezett.'),
        target: z.string().nullish(),
        rel: z.string().nullish(),
    }),
});
/**
 * Az alahuzas is ide tartozik.

 * A szerkeszto (StarterKit) alapbol tud alahuzni, es a beillesztett HTML-bol
 * is atveszi: a Teams-meghivo csatlakozasi linkje `text-decoration: underline`
 * stilussal jon, amibol alahuzas-jeloles lesz. Amig a sema nem ismerte, a
 * szerver az egesz uzenetet elutasitotta - "Ervenytelen keres" -, es az Ujra
 * gomb ugyanabba futott. Ha a szerkeszto eloallithatja, a semanak el kell
 * fogadnia.
 */
const simpleMark = z.object({
    type: z.enum(['bold', 'italic', 'underline', 'strike', 'code']),
});
export const markSchema = z.union([simpleMark, linkMark]);
// ─── Sorkozi csomopontok ──────────────────────────────────────────────────────
export const mentionKindSchema = z.enum(['user', 'channel', 'here', 'group']);
const mentionNode = z.object({
    type: z.literal('mention'),
    attrs: z.object({
        /** Felhasznalo-emlitesnel az azonosito, @channel/@here eseten maga a kulcsszo. */
        id: z.string().min(1).max(64),
        label: z.string().min(1).max(120),
        kind: mentionKindSchema,
    }),
});
const textNode = z.object({
    type: z.literal('text'),
    text: z.string().max(MAX_DOC_TEXT_LENGTH),
    marks: z.array(markSchema).max(8).optional(),
});
const hardBreakNode = z.object({ type: z.literal('hardBreak') });
const inlineNode = z.union([textNode, mentionNode, hardBreakNode]);
// ─── Blokkok ──────────────────────────────────────────────────────────────────
const paragraphNode = z.object({
    type: z.literal('paragraph'),
    content: z.array(inlineNode).max(500).optional(),
});
const codeBlockNode = z.object({
    type: z.literal('codeBlock'),
    attrs: z.object({ language: z.string().max(32).nullish() }).optional(),
    content: z.array(textNode).max(200).optional(),
});
/**
 * A GIF forrasat SZIGORUAN korlatozzuk.
 *
 * A dokumentumot barmelyik kliens elkuldheti, es minden kliens kirajzolja -
 * egy tetszoleges cimu `<img>` igy kovetokepp mukodne (a megnyitott uzenet
 * elarulna a nezo IP-cimet es idejet egy idegen szervernek). Ezert csak a
 * Giphy media-tarhelye engedett, ahonnan a kereso amugy is dolgozik.
 */
export const GIF_URL_RE = /^https:\/\/(media[0-9]*\.giphy\.com|i\.giphy\.com)\//;
const gifNode = z.object({
    type: z.literal('gif'),
    attrs: z.object({
        url: z.string().max(1024).regex(GIF_URL_RE, 'Csak Giphy-cím engedett.'),
        previewUrl: z.string().max(1024).regex(GIF_URL_RE, 'Csak Giphy-cím engedett.'),
        /**
         * Allo kocka - csokkentett mozgast kero nezonek ez jelenik meg.
         * Opcionalis: a regebbi uzenetekben nincs.
         */
        stillUrl: z.string().max(1024).regex(GIF_URL_RE, 'Csak Giphy-cím engedett.').nullish(),
        title: z.string().max(200).nullish(),
        width: z.number().int().min(1).max(4000),
        height: z.number().int().min(1).max(4000),
    }),
});
/** Egy talalat a GIF-kerésoböl - a valaszto ezt mutatja, es ebbol lesz a csomopont. */
export const gifResultSchema = z.object({
    id: z.string(),
    title: z.string().nullable(),
    url: z.string(),
    previewUrl: z.string(),
    /** Allo kockak csokkentett mozgashoz - ha a Giphy adott ilyet. */
    stillUrl: z.string().nullable(),
    previewStillUrl: z.string().nullable(),
    width: z.number(),
    height: z.number(),
});
/**
 * Lista adott elemtartalommal - a beagyazasi szinteket ebbol rakjuk ossze.
 *
 * ─── Miert nem eleg a "listaelemben csak bekezdes" szabaly? ─────────────────
 *
 * A szerkeszto (TipTap) listaeleme barmilyen blokkot befogad: beillesztett
 * Markdownbol a felsorolas alatti kodblokk es a Tab-bal beljebb huzott
 * allista is ide kerul. Amig a sema csak bekezdest engedett, a szerver az
 * egesz uzenetet elutasitotta - "Ervenytelen keres" -, es az Ujra gomb
 * ugyanabba futott. Ha a szerkeszto eloallithatja, a semanak el kell fogadnia.
 *
 * A melyseg megis FELULROL KORLATOS marad: rekurzio helyett a szinteket
 * egyenkent epitjuk fel, igy a legmelyebb szinten mar nincs tovabbi lista.
 */
const listsOf = (child) => {
    const item = z.object({
        type: z.literal('listItem'),
        content: z.array(child).max(20),
    });
    return {
        bullet: z.object({
            type: z.literal('bulletList'),
            content: z.array(item).max(200),
        }),
        ordered: z.object({
            type: z.literal('orderedList'),
            attrs: z.object({ start: z.number().int().min(1).max(9999).nullish() }).optional(),
            content: z.array(item).max(200),
        }),
    };
};
/** Negy szint: tobbet chatben senki nem agyaz, es a beillesztett szoveg sem. */
const lists4 = listsOf(z.union([paragraphNode, codeBlockNode]));
const lists3 = listsOf(z.union([paragraphNode, codeBlockNode, lists4.bullet, lists4.ordered]));
const lists2 = listsOf(z.union([paragraphNode, codeBlockNode, lists3.bullet, lists3.ordered]));
const lists1 = listsOf(z.union([paragraphNode, codeBlockNode, lists2.bullet, lists2.ordered]));
const bulletListNode = lists1.bullet;
const orderedListNode = lists1.ordered;
/** Az idezetbe is kerulhet kodblokk es lista - ugyanugy, mint a listaelembe. */
const blockquoteNode = z.object({
    type: z.literal('blockquote'),
    content: z
        .array(z.union([paragraphNode, codeBlockNode, bulletListNode, orderedListNode]))
        .max(50),
});
const blockNode = z.union([
    paragraphNode,
    codeBlockNode,
    gifNode,
    blockquoteNode,
    bulletListNode,
    orderedListNode,
]);
export const richTextDocSchema = z.object({
    type: z.literal('doc'),
    content: z.array(blockNode).max(300).optional(),
});
/**
 * Sima szoveg eloallitasa a dokumentumbol.
 *
 * Ezt MINDIG a szerver szamolja ki, sosem a klienstol vesszuk at: ebbol keszul a
 * kereses indexe, az ertesites szovege es az e-mail kivonat. Ha a kliens adhatna
 * meg, a megjelenitett es a keresheto tartalom elterhetne egymastol.
 */
export function docToPlainText(doc) {
    const lines = [];
    const walkInline = (nodes) => (nodes ?? [])
        .map((node) => {
        if (node.type === 'text')
            return node.text ?? '';
        if (node.type === 'hardBreak')
            return '\n';
        if (node.type === 'mention') {
            const kind = node.attrs?.kind;
            const label = String(node.attrs?.label ?? '');
            return kind === 'user' ? `@${label}` : `@${String(node.attrs?.id ?? label)}`;
        }
        return '';
    })
        .join('');
    const walkBlock = (node, prefix = '') => {
        switch (node.type) {
            case 'paragraph':
                lines.push(prefix + walkInline(node.content));
                break;
            case 'codeBlock':
                lines.push(walkInline(node.content));
                break;
            /**
             * A GIF-nek is kell szoveges nyoma.
             *
             * Ebbol keszul a kereses indexe es az ertesites szovege - enelkul egy
             * csak-GIF uzenet ures sornak latszana az ertesitesben.
             */
            case 'gif':
                lines.push(`[GIF${node.attrs?.title ? `: ${String(node.attrs.title)}` : ''}]`);
                break;
            case 'blockquote':
                for (const child of node.content ?? [])
                    walkBlock(child, '> ');
                break;
            case 'bulletList':
                for (const item of node.content ?? []) {
                    for (const child of item.content ?? [])
                        walkBlock(child, '• ');
                }
                break;
            case 'orderedList': {
                let index = Number(node.attrs?.start ?? 1);
                for (const item of node.content ?? []) {
                    for (const child of item.content ?? [])
                        walkBlock(child, `${index}. `);
                    index += 1;
                }
                break;
            }
            default:
                break;
        }
    };
    for (const block of doc.content ?? [])
        walkBlock(block);
    return lines.join('\n').trim();
}
/** Az emlitesek kinyerese - ebbol keszul majd az ertesites es a kiemeles. */
export function extractMentions(doc) {
    const userIds = new Set();
    const groupIds = new Set();
    let channelWide = null;
    const walk = (node) => {
        if (node.type === 'mention') {
            const kind = node.attrs?.kind;
            const id = String(node.attrs?.id ?? '');
            if (kind === 'user' && id)
                userIds.add(id);
            if (kind === 'group' && id)
                groupIds.add(id);
            // A @channel erosebb jelzes, ezert nem irja felul a @here.
            if (kind === 'channel')
                channelWide = 'channel';
            else if (kind === 'here' && channelWide === null)
                channelWide = 'here';
        }
        for (const child of node.content ?? [])
            walk(child);
    };
    walk(doc);
    return { userIds: [...userIds], groupIds: [...groupIds], channelWide };
}
/**
 * A dokumentum merete mufaj szerint bontva.
 *
 * A kodblokkok tartalma a `code`, minden mas a `prose`. A ketto kulon
 * korlatot kap, igy egy hosszu kodreszlet nem eszi el a szoveg helyet, es
 * forditva sem.
 */
export function measureDoc(doc) {
    let prose = 0;
    let code = 0;
    const walk = (node, inCode) => {
        const isCode = inCode || node.type === 'codeBlock';
        if (node.type === 'text') {
            const length = (node.text ?? '').length;
            if (isCode)
                code += length;
            else
                prose += length;
        }
        for (const child of node.content ?? [])
            walk(child, isCode);
    };
    walk(doc, false);
    return { prose, code };
}
/** Csomopontok szama - a tul nagy dokumentumok elutasitasahoz. */
export function countNodes(doc) {
    let count = 0;
    const walk = (node) => {
        count += 1;
        for (const child of node.content ?? [])
            walk(child);
    };
    walk(doc);
    return count;
}
/** Sima szovegbol egyszeru dokumentum - botok es webhookok ezt hasznaljak. */
export function plainTextToDoc(text) {
    return {
        type: 'doc',
        content: text.split('\n').map((line) => ({
            type: 'paragraph',
            ...(line ? { content: [{ type: 'text', text: line }] } : {}),
        })),
    };
}
//# sourceMappingURL=richtext.js.map